import { hrmsSupabase, supabase } from '../../../services/supabase';
import { SprintCheckInResponse, RawTimelineData } from '../types/timeline';

export type Tleaves = {
  created_at: string;
  employee_id: string;
  end_date: string;
  keka_leave_id: string;
  start_date: string;
  status: string;
  updated_at: string;
};

export type TOOOData = {
  id: string;
  type: string;
  reason: string;
};

interface TicketWithCheckIn {
  id: string;
  ticket_number: string;
  title: string;
  external_link: string;
  created_at: string;
  latest_checkin: Array<{
    completion_percentage: number;
    check_ins: Array<{
      user_id: string;
    }>;
  }>;
}

export async function fetchCheckIns(
  memberIds: string[],
  date: string,
): Promise<any> {
  return supabase
    .from('check_ins')
    .select(
      `
      id,
      user_id,
      daily_sequence,
      ticket_check_ins (
        id,
        check_in_type,
        title,
        ticket:ticket_id (
          id,
          ticket_number,
          title,
          external_link,
          created_at
        ),
        completion_percentage,
        previous_completion_percentage,
        description,
        blockers,
        wip_links
      )
    `,
    )
    .in('user_id', memberIds)
    .gte('check_in_time', `${date}T00:00:00`)
    .lte('check_in_time', `${date}T23:59:59`);
}

export async function fetchMeetings(
  memberIds: string[],
  date: string,
): Promise<any> {
  return supabase
    .from('calendar_events')
    .select('*')
    .in('user_id', memberIds)
    .or(
      `and(start_time.gte.${date}T00:00:00,start_time.lt.${date}T23:59:59),` + // Starts on this day
        `and(end_time.gt.${date}T00:00:00,end_time.lte.${date}T23:59:59),` + // Ends on this day
        `and(start_time.lte.${date}T00:00:00,end_time.gte.${date}T23:59:59)`, // Spans across this day
    );
}

export async function fetchSprintTimeline(
  memberId: string,
  employeeId: string,
  sprintIds: string[],
  startDate: string,
  endDate: string,
  shadowTo?: string,
): Promise<RawTimelineData> {
  // const { data: sprint, error: sprintError } = await supabase
  //   .from('sprints')
  //   .select<string, { start_date: string; end_date: string }>(
  //     'start_date, end_date',
  //   )
  //   .in('id', sprintIds)
  //   .order('start_date', { ascending: true });

  // if (sprintError) {
  //   throw new Error(`Failed to fetch sprint: ${sprintError.message}`);
  // }

  // First fetch all tickets assigned to the user in this sprint
  const { data: tickets, error: ticketsError } = await supabase
    .from('tickets')
    .select(
      `
      id,
      ticket_number,
      title,
      external_link,
      created_at,
      latest_checkin:ticket_check_ins (
        completion_percentage,
        check_ins!inner (
          user_id
        )
      )
    `,
    )
    .eq('assigned_to', shadowTo || memberId)
    .in('sprint_id', sprintIds)
    .eq('latest_checkin.check_ins.user_id', memberId)
    .order('created_at', { foreignTable: 'latest_checkin', ascending: false })
    .limit(1, { foreignTable: 'latest_checkin' });

  if (ticketsError) {
    throw new Error(`Failed to fetch tickets: ${ticketsError.message}`);
  }

  // Then fetch check-ins for these tickets
  const { data: checkIns, error: checkInsError } = await supabase
    .from('check_ins')
    .select(
      `
      id,
      daily_sequence,
      created_at,
      ticket_check_ins (
        id,
        check_in_type,
        title,
        ticket:ticket_id (
          id,
          ticket_number,
          title,
          external_link
        ),
        description,
        wip_links,
        completion_percentage,
        previous_completion_percentage,
        blockers
      )
    `,
    )
    .eq('user_id', memberId)
    .gte('check_in_time', `${startDate}T00:00:00`)
    .lte('check_in_time', `${endDate}T23:59:59`);

  if (checkInsError) {
    throw new Error(`Failed to fetch check-ins: ${checkInsError.message}`);
  }

  // Fetch meetings
  const { data: meetings, error: meetingsError } = await supabase
    .from('calendar_events')
    .select('*')
    .eq('user_id', memberId)
    .or(
      `and(start_time.gte.'${startDate}T00:00:00',start_time.lte.'${endDate}T23:59:59'),` + // Starts within range
        `and(end_time.gte.'${startDate}T00:00:00',end_time.lte.'${endDate}T23:59:59'),` + // Ends within range
        `and(start_time.lte.'${startDate}T00:00:00',end_time.gte.'${endDate}T23:59:59')`, // Spans across range
    );

  if (meetingsError) {
    throw new Error(`Failed to fetch meetings: ${meetingsError.message}`);
  }

  const { data: leaves, error: leavesError } = await hrmsSupabase
    .from('leaves')
    .select('*')
    .eq('employee_id', employeeId)
    .or(
      `and(start_date.gte.'${startDate}T00:00:00',start_date.lte.'${endDate}T23:59:59'),` + // Starts within range
        `and(end_date.gte.'${startDate}T00:00:00',end_date.lte.'${endDate}T23:59:59'),` + // Ends within range
        `and(start_date.lte.'${startDate}T00:00:00',end_date.gte.'${endDate}T23:59:59')`, // Spans across range
    );

  if (leavesError) {
    throw new Error(`Failed to fetch leaves: ${leavesError.message}`);
  }

  return {
    startDate,
    endDate,
    tickets: tickets.map((ticket: TicketWithCheckIn) => ({
      ...ticket,
      completion_percentage:
        ticket.latest_checkin[0]?.completion_percentage || 0,
    })),
    checkIns: checkIns as unknown as SprintCheckInResponse[],
    meetings: (meetings || []) as RawTimelineData['meetings'],
    leaves: (leaves || []) as RawTimelineData['leaves'],
  };
}

export async function fetchOOOData(
  selectedMembers: { memberId: string; employeeId: string }[],
  date: string,
): Promise<string[]> {
  const { data, error } = await hrmsSupabase
    .from('leaves')
    .select('*')
    .in(
      'employee_id',
      selectedMembers.map((member) => member.employeeId),
    )
    .lte('start_date', `${date}T00:00:00`)
    .gte('end_date', `${date}T00:00:00`);
  if (error) {
    throw new Error(`Failed to fetch ooo data: ${error.message}`);
  }
  const oooDataMap = new Map<string, Tleaves>();
  if (data) {
    data.forEach((item: Tleaves) => {
      if (item?.status === 'approved' || item?.status === 'pending') {
        oooDataMap.set(item.employee_id, item);
      }
    });
  }

  const oooData = selectedMembers
    .map((member) => {
      const leave = oooDataMap.get(member.employeeId);
      return leave ? member.memberId : null;
    })
    .filter((item) => item !== null);

  return oooData as string[];
}
